import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import { HTTP_UNAUTHORIZED } from "../lib/constants";
import {
  Calendar,
  Mail,
  Euro,
  LogOut,
  CheckCircle2,
  XCircle,
  Clock,
  Image as ImageIcon,
  Home,
  Type,
  Settings as SettingsIcon,
  Inbox,
  RefreshCw,
} from "lucide-react";
import PhotosTab from "../components/admin/PhotosTab";
import ApartmentsTab from "../components/admin/ApartmentsTab";
import SettingsTab from "../components/admin/SettingsTab";
import TextsTab from "../components/admin/TextsTab";
import ICalTab from "../components/admin/ICalTab";
import PricingTab from "../components/admin/PricingTab";
import RefundDialog from "../components/admin/RefundDialog";
import CancelDialog from "../components/admin/CancelDialog";

function StatusBadge({ s }) {
  const map = {
    confirmed: { c: "bg-emerald-100 text-emerald-700", Icon: CheckCircle2 },
    pending: { c: "bg-amber-100 text-amber-700", Icon: Clock },
    cancelled: { c: "bg-red-100 text-red-700", Icon: XCircle },
  };
  const { c, Icon } = map[s] || map.pending;
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs ${c}`}>
      <Icon className="w-3 h-3" />
      {s}
    </span>
  );
}

function BookingsTab({ bookings, onChange }) {
  const [refundFor, setRefundFor] = useState(null);
  const [cancelFor, setCancelFor] = useState(null);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/bookings/${id}`, { status });
      toast.success("Updated");
      onChange();
    } catch {
      toast.error("Error");
    }
  };

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-[#F5F2EB] text-[#6B705C] uppercase text-xs tracking-widest">
          <tr>
            <th className="text-left px-5 py-3">Guest</th>
            <th className="text-left px-5 py-3">Apt</th>
            <th className="text-left px-5 py-3">Dates</th>
            <th className="text-left px-5 py-3">Total</th>
            <th className="text-left px-5 py-3">Status</th>
            <th className="text-left px-5 py-3">Payment</th>
            <th className="text-left px-5 py-3">Actions</th>
          </tr>
        </thead>
        <tbody>
          {bookings.map((b) => (
            <tr key={b.id} className="border-t border-[#E9E2D3]" data-testid={`booking-row-${b.id}`}>
              <td className="px-5 py-4">
                <div className="text-[#1A2B4C]">{b.guest_name}</div>
                <div className="text-xs text-[#6B705C]">{b.guest_email}</div>
              </td>
              <td className="px-5 py-4 text-[#1A2B4C]">{b.apartment_name}</td>
              <td className="px-5 py-4 text-[#1A2B4C]">
                {b.check_in} → {b.check_out}
                <div className="text-xs text-[#6B705C]">{b.nights}n · {b.adults + b.children} guests</div>
              </td>
              <td className="px-5 py-4 font-serif text-[#1A2B4C]">€{b.total_amount}</td>
              <td className="px-5 py-4"><StatusBadge s={b.status} /></td>
              <td className="px-5 py-4">
                <span className={`text-xs ${b.payment_status === "paid" ? "text-emerald-600" : "text-amber-600"}`}>
                  {b.payment_status}
                </span>
              </td>
              <td className="px-5 py-4">
                <div className="flex flex-wrap gap-1">
                  <button
                    onClick={() => updateStatus(b.id, "confirmed")}
                    className="text-xs px-2 py-1 rounded-md bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                  >
                    Confirm
                  </button>
                  <button
                    onClick={() => setCancelFor(b)}
                    className="text-xs px-2 py-1 rounded-md bg-red-50 text-red-700 hover:bg-red-100"
                    data-testid={`cancel-${b.id}`}
                    disabled={b.status === "cancelled"}
                  >
                    Cancel
                  </button>
                  {b.payment_status === "paid" && (
                    <button
                      onClick={() => setRefundFor(b)}
                      className="text-xs px-2 py-1 rounded-md bg-amber-50 text-amber-700 hover:bg-amber-100 border border-amber-200"
                      data-testid={`refund-${b.id}`}
                      title="Cancel & refund"
                    >
                      💸 Refund
                    </button>
                  )}
                  {(b.payment_status === "refunded" || b.payment_status === "partially_refunded") && (
                    <span className="text-xs px-2 py-1 rounded-md bg-gray-100 text-gray-600">
                      {b.payment_status === "refunded" ? "Refunded" : "Partial refund"}
                    </span>
                  )}
                </div>
              </td>
            </tr>
          ))}
          {bookings.length === 0 && (
            <tr><td colSpan="7" className="px-5 py-10 text-center text-[#6B705C]">No bookings yet.</td></tr>
          )}
        </tbody>
      </table>
      {refundFor && (
        <RefundDialog
          booking={refundFor}
          onClose={() => setRefundFor(null)}
          onDone={onChange}
        />
      )}
      {cancelFor && (
        <CancelDialog
          booking={cancelFor}
          onClose={() => setCancelFor(null)}
          onDone={onChange}
        />
      )}
    </div>
  );
}

function MessagesTab({ messages }) {
  return (
    <div className="divide-y divide-[#E9E2D3]">
      {messages.map((m) => (
        <div key={m.id} className="p-6" data-testid={`message-${m.id}`}>
          <div className="flex justify-between mb-2">
            <div>
              <p className="text-[#1A2B4C] font-medium">{m.name}</p>
              <p className="text-xs text-[#6B705C]">{m.email} · {m.phone}</p>
            </div>
            <p className="text-xs text-[#6B705C]">{new Date(m.created_at).toLocaleString()}</p>
          </div>
          {(m.check_in || m.check_out) && (
            <p className="text-xs text-[#6B705C] mb-2">{m.check_in} → {m.check_out} · {m.guests} guests</p>
          )}
          <p className="text-sm text-gray-700">{m.message}</p>
        </div>
      ))}
      {messages.length === 0 && (
        <div className="p-10 text-center text-[#6B705C]">No messages yet.</div>
      )}
    </div>
  );
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [messages, setMessages] = useState([]);
  const [tab, setTab] = useState("bookings");

  const load = useCallback(async () => {
    try {
      const [s, b, m] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/bookings"),
        api.get("/admin/messages"),
      ]);
      setStats(s.data);
      setBookings(b.data);
      setMessages(m.data);
    } catch (e) {
      if (e?.response?.status === HTTP_UNAUTHORIZED) navigate("/admin/login");
    }
  }, [navigate]);

  useEffect(() => {
    if (!localStorage.getItem("admin_token")) {
      navigate("/admin/login");
      return;
    }
    load();
  }, [load, navigate]);

  const logout = () => {
    localStorage.removeItem("admin_token");
    navigate("/admin/login");
  };

  const tabs = [
    { id: "bookings", label: "Bookings", Icon: Calendar, count: bookings.length },
    { id: "messages", label: "Messages", Icon: Inbox, count: messages.length },
    { id: "photos", label: "Photos", Icon: ImageIcon },
    { id: "apartments", label: "Apartments", Icon: Home },
    { id: "pricing", label: "Pricing", Icon: Euro },
    { id: "ical", label: "Calendar Sync", Icon: RefreshCw },
    { id: "texts", label: "Texts", Icon: Type },
    { id: "settings", label: "Settings", Icon: SettingsIcon },
  ];

  return (
    <div className="min-h-screen bg-[#F5F2EB]">
      <header className="bg-white border-b border-[#E9E2D3] sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 py-5 flex items-center justify-between">
          <div>
            <p className="text-xs uppercase tracking-widest text-[#6B705C]">Admin</p>
            <h1 className="font-serif text-2xl text-[#1A2B4C]">Sithonia Holidays</h1>
          </div>
          <div className="flex items-center gap-3">
            <a
              href="/"
              target="_blank"
              rel="noreferrer"
              className="text-sm text-[#6B705C] hover:text-[#1A2B4C] hidden sm:inline"
            >
              View site →
            </a>
            <button
              onClick={logout}
              data-testid="admin-logout"
              className="flex items-center gap-2 text-sm text-[#1A2B4C] border border-[#E9E2D3] px-4 py-2 rounded-full hover:bg-[#1A2B4C] hover:text-white transition-all"
            >
              <LogOut className="w-4 h-4" />
              Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-10">
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
            {[
              { label: "Total", v: stats.bookings_total, Icon: Calendar },
              { label: "Confirmed", v: stats.confirmed, Icon: CheckCircle2 },
              { label: "Pending", v: stats.pending, Icon: Clock },
              { label: "Messages", v: stats.messages, Icon: Mail },
              { label: "Revenue", v: `€${stats.revenue}`, Icon: Euro },
            ].map(({ label, v, Icon }) => (
              <div key={label} data-testid={`stat-${label.toLowerCase()}`} className="bg-white rounded-2xl p-5 border border-[#E9E2D3]">
                <Icon className="w-4 h-4 text-[#6B705C] mb-3" />
                <p className="text-xs uppercase tracking-widest text-[#6B705C]">{label}</p>
                <p className="font-serif text-2xl text-[#1A2B4C] mt-1">{v}</p>
              </div>
            ))}
          </div>
        )}

        <div className="bg-white rounded-2xl border border-[#E9E2D3] overflow-hidden">
          <div className="border-b border-[#E9E2D3] flex overflow-x-auto">
            {tabs.map(({ id, label, Icon, count }) => (
              <button
                key={id}
                onClick={() => setTab(id)}
                data-testid={`tab-${id}`}
                className={`flex items-center gap-2 px-5 py-4 text-sm uppercase tracking-widest whitespace-nowrap ${
                  tab === id ? "text-[#1A2B4C] border-b-2 border-[#1A2B4C]" : "text-[#6B705C]"
                }`}
              >
                <Icon className="w-4 h-4" />
                {label}
                {typeof count === "number" && <span className="opacity-70">({count})</span>}
              </button>
            ))}
          </div>

          {tab === "bookings" && <BookingsTab bookings={bookings} onChange={load} />}
          {tab === "messages" && <MessagesTab messages={messages} />}
          {tab === "photos" && <PhotosTab />}
          {tab === "apartments" && <ApartmentsTab />}
          {tab === "pricing" && <PricingTab />}
          {tab === "ical" && <ICalTab />}
          {tab === "texts" && <TextsTab />}
          {tab === "settings" && <SettingsTab />}
        </div>
      </main>
    </div>
  );
}
